/**
 * Shell do layout Simplificado (spec 018, US4): barra lateral ESTÁVEL de 7
 * destinos, área central, e o resto sob demanda. Medido em `5c943de`: 11 itens
 * na barra do Completo. O registro dos destinos mora em ./simple-nav (puro —
 * a guarda da API prova que nenhuma rota do Completo fica sem destino aqui).
 *
 * As telas do Completo que o Simplificado não redesenhou (Clientes, Agenda,
 * Relatórios, Configurações…) montam AQUI dentro, sem quebrar navegação (017,
 * US3 AS2); a aba no topo diz onde se está quando o destino junta mais de uma.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  CalendarDays,
  Inbox,
  LayoutTemplate,
  LogOut,
  MessageCircle,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  Settings,
  Sun,
  UserRound,
  Users2,
  type LucideIcon,
} from 'lucide-react';
import { PG_INT_MAX, UI_LAYOUT_LABELS, UI_LAYOUTS, capitalize, type UiLayout } from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { NAV_ANY_OF } from '@/shared/nav-permissions';
import { performLogout } from '@/auth/logout';
import { NotificationsBell } from '@/shared/notifications-bell';
import { NewsMegaphone } from '@/features/novidades/NewsMegaphone';
import { AppBrandMark } from '@/shared/brand-mark';
import { ticketSearchHint, useTenantProfile } from '@/shared/tenant-profile';
import { useUiPrefs } from '@/shared/ui-prefs';
import { useRealtimeSync } from '@/shared/use-realtime-sync';
import { installNotificationSoundPriming } from '@/shared/notification-sound';
import { ChatsNavBadge } from '@/features/chats/chats-unread-badge';
import { PresenceChip } from '@/features/presence/presence-chip';
import { NewTicketDialog } from '@/features/tickets/NewTicketDialog';
import { creatableDesks, desksListQuery, useOperatedClientIds } from '@/features/tickets/tickets-queries';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  clearSelectedTenant,
  getKnownTenants,
  getSelectedTenant,
  tenantLabel,
} from '@/lib/tenant-selection';
import {
  destinationFor,
  tabFor,
  visibleDestinations,
  visibleTabs,
  type DestinationKey,
  type NavLabel,
  type NavTab,
} from './simple-nav';

const ICONS: Record<DestinationKey, LucideIcon> = {
  hoje: Sun,
  atendimentos: Inbox,
  conversas: MessageCircle,
  agenda: CalendarDays,
  clientes: Users2,
  conhecimentos: BookOpen,
  gestao: BarChart3,
  configuracoes: Settings,
};

export function useNavLabel(): (label: NavLabel) => string {
  const terms = useTenantProfile().terms;
  return (label) => (typeof label === 'string' ? label : capitalize(terms[label.term]));
}

export function SimpleShell() {
  // Stream SSE e priming do som: os mesmos do Completo — só um shell monta
  useRealtimeSync();
  useEffect(() => installNotificationSoundPriming(), []);
  const user = useAuthUser();
  const { can, ready: permissionsReady } = usePermissions();
  // spec 058: o Novo ticket e a busca são de quem vê tickets
  const ticketsTeam = permissionsReady && NAV_ANY_OF.newTicket.some((key) => can(key));
  const ui = useUiPrefs();
  const label = useNavLabel();
  const ticketTerm = useTenantProfile().terms.ticket;
  const location = useRouterState({ select: (s) => s.location });
  const [collapsed, setCollapsed] = useState(false);
  const serverCollapsed = ui.prefs?.sidebarCollapsed;
  useEffect(() => {
    if (serverCollapsed !== undefined) setCollapsed(serverCollapsed);
  }, [serverCollapsed]);
  const [newOpen, setNewOpen] = useState(false);
  const desksQ = useQuery(desksListQuery());
  const desks = desksQ.data ?? [];
  // spec 011 → 024: mesa do ERP, ou mesa espelhada com cliente operado no ERP
  const operated = useOperatedClientIds();
  const creatable = creatableDesks(desks, operated, null).length > 0;

  // enquanto o /me não chega, item com permissão fica escondido (aparecer
  // depois é melhor que aparecer-e-sumir — a regra do Completo)
  const destinations = visibleDestinations((k) => permissionsReady && can(k));
  const current = destinationFor(location.pathname);
  const search = location.search as Record<string, unknown>;

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    ui.update({ sidebarCollapsed: next });
  };

  return (
    <TooltipProvider>
      <div className="flex h-dvh overflow-hidden bg-background">
        <aside
          className={cn(
            'flex h-full shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[width] duration-200',
            collapsed ? 'w-16' : 'w-60'
          )}
        >
          <div className={cn('flex h-14 shrink-0 items-center', collapsed ? 'justify-center' : 'px-5')}>
            <Link to="/s/hoje" aria-label="Ir para Hoje" className="min-w-0">
              <AppBrandMark compact={collapsed} className="text-[21px] text-sidebar-foreground" />
            </Link>
          </div>

          <div className={cn('flex flex-col gap-2 pb-2', collapsed ? 'px-2' : 'px-3', !ticketsTeam && 'hidden')}>
            <SidebarButton
              collapsed={collapsed}
              label={`Novo ${ticketTerm}`}
              disabled={!creatable}
              title={creatable ? undefined : 'Todas as mesas são espelhadas do TiFlux — abra pelo TiFlux'}
              onClick={() => setNewOpen(true)}
            />
            {collapsed ? null : <SidebarSearch />}
          </div>

          <nav className="mt-1 min-h-0 flex-1 overflow-y-auto px-2" aria-label="Destinos">
            {[1, 2, 3].map((group) => {
              const items = destinations.filter((x) => x.destination.group === group);
              if (items.length === 0) return null;
              return (
                <ul key={group} className={cn('flex flex-col gap-0.5', group > 1 && 'mt-2 border-t border-white/10 pt-2')}>
                  {items.map(({ destination: d, tabs }) => {
                    const Icon = ICONS[d.key];
                    const first = tabs[0]!;
                    const active = current?.key === d.key;
                    const text = label(d.label);
                    const link = (
                      <Link
                        to={first.to}
                        search={first.search ?? {}}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'relative flex h-10 items-center gap-3 rounded-md text-sm font-medium transition-colors',
                          collapsed ? 'justify-center px-0' : 'px-3',
                          active ? 'bg-white/10 text-white' : 'text-white/75 hover:bg-white/5 hover:text-white'
                        )}
                      >
                        <Icon className="size-[18px] shrink-0" />
                        {!collapsed && <span className="truncate">{text}</span>}
                        {d.key === 'conversas' ? <ChatsNavBadge collapsed={collapsed} /> : null}
                      </Link>
                    );
                    return (
                      <li key={d.key}>
                        {collapsed ? (
                          <Tooltip>
                            <TooltipTrigger asChild>{link}</TooltipTrigger>
                            <TooltipContent side="right">{text}</TooltipContent>
                          </Tooltip>
                        ) : (
                          link
                        )}
                      </li>
                    );
                  })}
                </ul>
              );
            })}
          </nav>

          <div
            className={cn(
              'flex shrink-0 items-center gap-2.5 border-t border-white/10 py-3',
              collapsed ? 'justify-center px-0' : 'px-4'
            )}
          >
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
              {(user?.name?.trim().charAt(0) ?? '?').toUpperCase()}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-white">{user?.name}</div>
                <div className="truncate text-xs text-white/50">{user?.email}</div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expandir menu' : 'Minimizar menu'}
            className={cn(
              'flex h-11 shrink-0 items-center gap-2.5 bg-sidebar-footer text-[13px] text-white/70 transition-colors hover:text-white',
              collapsed ? 'justify-center px-0' : 'px-4'
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <>
                <PanelLeftClose className="size-4" />
                Minimizar
              </>
            )}
          </button>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-12 shrink-0 items-stretch justify-between gap-3 border-b bg-card">
            <SectionTabs
              tabs={current ? visibleTabs(current, (k) => permissionsReady && can(k)) : []}
              active={current ? tabFor(current, location.pathname, search) : null}
              label={label}
            />
            <div className="flex shrink-0 items-center gap-4 pr-4">
              <PresenceChip />
              <NewsMegaphone audience="staff" />
              <NotificationsBell />
              <UserMenu />
            </div>
          </header>
          {/* a rolagem do conteúdo é deste elemento: o id deixa o roteador
              devolvê-la ao voltar para a lista (US2 AS2) */}
          <main data-scroll-restoration-id="simple-main" className="min-h-0 flex-1 overflow-y-auto">
            <div className="p-4">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      {newOpen ? (
        <NewTicketDialog desks={desks} defaultDeskId={undefined} onClose={() => setNewOpen(false)} />
      ) : null}
    </TooltipProvider>
  );
}

function SidebarButton({
  collapsed,
  label,
  disabled,
  title,
  onClick,
}: {
  collapsed: boolean;
  label: string;
  disabled: boolean;
  title: string | undefined;
  onClick: () => void;
}) {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={collapsed ? undefined : title}
      aria-label={collapsed ? label : undefined}
      className={cn(
        'flex h-9 w-full items-center justify-center gap-1.5 rounded-md bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover active:bg-primary-pressed disabled:cursor-not-allowed disabled:opacity-60'
      )}
    >
      <Plus className="size-4" strokeWidth={2.5} />
      {collapsed ? null : capitalize(label)}
    </button>
  );
  if (!collapsed) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">{title ?? capitalize(label)}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Busca da barra (⌘K / Ctrl+K foca): número abre o atendimento; o resto —
 * assunto, cliente, contato, placa/identificador, telefone — abre a lista em
 * TODAS as mesas com a busca (a mesma da spec 012).
 */
function SidebarSearch() {
  const navigate = useNavigate();
  const hint = ticketSearchHint(useTenantProfile());
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setValue('');
      inputRef.current?.blur();
      return;
    }
    if (e.key !== 'Enter') return;
    const raw = value.trim();
    if (!raw) return;
    setValue('');
    const number = raw.replace(/^#/, '');
    if (/^\d+$/.test(number) && Number(number) <= PG_INT_MAX) {
      void navigate({ to: '/s/atendimentos/$ticketNumber', params: { ticketNumber: number } });
      return;
    }
    void navigate({ to: '/s/atendimentos', search: { q: raw, status: 'all' } });
  };

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-white/50" />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Buscar"
        aria-label={`Buscar pelo número, ${hint}`}
        title={`Nº, ${hint}`}
        className="h-9 w-full rounded-md border border-white/10 bg-white/5 pr-12 pl-8 text-sm text-white outline-none transition-colors placeholder:text-white/50 focus:border-primary focus:bg-white/10"
      />
      <kbd className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 rounded border border-white/15 px-1 text-[10px] text-white/50">
        Ctrl K
      </kbd>
    </div>
  );
}

/** Abas do destino que junta mais de uma tela (Clientes, Gestão, Atendimentos). */
function SectionTabs({
  tabs,
  active,
  label,
}: {
  tabs: NavTab[];
  active: NavTab | null;
  label: (l: NavLabel) => string;
}) {
  if (tabs.length < 2) return <div className="min-w-0 flex-1" />;
  return (
    <nav aria-label="Seções" className="flex min-w-0 flex-1 items-end gap-1 overflow-x-auto pl-4">
      {tabs.map((tab) => {
        const isActive = active?.key === tab.key;
        return (
          <Link
            key={tab.key}
            to={tab.to}
            search={tab.search ?? {}}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'flex h-9 shrink-0 items-center rounded-t-md px-4 text-[13px] font-medium transition-colors',
              isActive ? 'bg-background text-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {label(tab.label)}
          </Link>
        );
      })}
    </nav>
  );
}

function UserMenu() {
  const user = useAuthUser();
  const ui = useUiPrefs();
  const navigate = useNavigate();
  const initial = (user?.name?.trim().charAt(0) ?? '?').toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Menu do usuário"
          className="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          {initial}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="flex items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {initial}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{user?.name}</span>
            <span className="block truncate text-xs font-normal text-muted-foreground">{user?.email}</span>
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void navigate({ to: '/account' })}>
          <UserRound />
          Minha conta
        </DropdownMenuItem>
        {getKnownTenants().length > 1 ? (
          <DropdownMenuItem
            onSelect={() => {
              clearSelectedTenant();
              window.location.reload();
            }}
          >
            <ArrowLeftRight />
            Trocar empresa
            <span className="ml-auto text-xs text-muted-foreground">{tenantLabel(getSelectedTenant() ?? '')}</span>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <LayoutTemplate className="size-3.5" />
          Interface
        </DropdownMenuLabel>
        {/* a troca vale na hora; o LayoutRoot leva a tela para o equivalente */}
        <DropdownMenuRadioGroup value={ui.layout} onValueChange={(value) => ui.update({ layout: value as UiLayout })}>
          {UI_LAYOUTS.map((layout) => (
            <DropdownMenuRadioItem key={layout} value={layout} disabled={!ui.ready}>
              {UI_LAYOUT_LABELS[layout]}
              {layout === ui.defaultLayout ? <span className="ml-auto text-xs text-muted-foreground">padrão</span> : null}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => performLogout(() => void navigate({ to: '/login' }))}>
          <LogOut />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
